/**
 * Context Collector
 * Collects browser, device, and user context information
 */

class ContextCollector {
  /**
   * Initialize ContextCollector
   * @param {Object} config - SDK configuration
   */
  constructor(config) {
    this.config = config;
    
    // Initialize context storage
    this.context = {
      browser: null,
      device: null,
      os: null,
      screen: null,
      user: null,
      custom: {}
    };
    
    // Generic value patterns for validation
    this.genericPatterns = {
      deviceModel: [
        /^Nexus \d+/i,
        /^Build\//i,
        /^Linux Device$/i,
        /^Android Device$/i,
        /^Generic/i,
        /^unknown$/i,
        /^Tablet$/i
      ],
      osName: [
        /^Linux unknown$/i,
        /^Unknown OS$/i,
        /^unknown$/i
      ]
    };
  }

  /**
   * Capture initial context on SDK initialization
   * @public
   */
  captureInitialContext() {
    this.context.browser = this.getBrowserInfo();
    this.context.device = this.getDeviceInfo();
    this.context.os = this.getOSInfo();
    this.context.screen = this.getScreenInfo();
    
    // Set up network status monitoring
    this.setupNetworkMonitoring();
  }

  /**
   * Set up network status monitoring
   * @private
   */
  setupNetworkMonitoring() {
    if (typeof window === 'undefined' || !window.navigator) {
      return;
    }

    // Update network status when it changes
    const updateNetworkStatus = () => {
      if (this.context.device) {
        this.context.device.online = window.navigator.onLine;
      }
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);
  }

  /**
   * Get browser information
   * @returns {Object} - Browser details
   * @private
   */
  getBrowserInfo() {
    if (typeof window === 'undefined' || !window.navigator) {
      return null;
    }

    const ua = window.navigator.userAgent;
    let browserName = null;
    let browserVersion = null;
    let engineName = null;

    // Detect browser and version with improved accuracy
    // Order matters - check most specific first
    if (ua.includes('Edg/')) {
      // New Chromium-based Edge
      browserName = 'Edge';
      const match = ua.match(/Edg\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'Blink';
    } else if (ua.includes('Edge/')) {
      // Legacy Edge
      browserName = 'Edge Legacy';
      const match = ua.match(/Edge\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'EdgeHTML';
    } else if (ua.includes('Chrome/') && !ua.includes('Chromium/')) {
      browserName = 'Chrome';
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'Blink';
    } else if (ua.includes('Chromium/')) {
      browserName = 'Chromium';
      const match = ua.match(/Chromium\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'Blink';
    } else if (ua.includes('Firefox/')) {
      browserName = 'Firefox';
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'Gecko';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      browserName = 'Safari';
      const match = ua.match(/Version\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = 'WebKit';
    } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
      browserName = 'Opera';
      const match = ua.match(/(?:Opera\/|OPR\/)(\d+\.\d+)/);
      browserVersion = match ? match[1] : null;
      engineName = ua.includes('OPR/') ? 'Blink' : 'Presto';
    }

    // Only return browser info if we successfully detected it
    if (!browserName) {
      return null;
    }

    return {
      name: browserName,
      version: browserVersion,
      engine: engineName,
      language: window.navigator.language || null,
      userAgent: ua,
      cookiesEnabled: window.navigator.cookieEnabled || false
    };
  }

  /**
   * Get operating system information
   * @returns {Object} - OS details
   * @private
   */
  getOSInfo() {
    if (typeof window === 'undefined' || !window.navigator) {
      return null;
    }

    const ua = window.navigator.userAgent;
    let osName = null;
    let osVersion = null;
    let platform = window.navigator.platform || null;

    // Detect OS - be specific or return null
    if (ua.includes('Windows')) {
      osName = 'Windows';
      const match = ua.match(/Windows NT (\d+\.\d+)/);
      osVersion = match ? match[1] : null;
    } else if (ua.includes('Mac OS X')) {
      osName = 'macOS';
      const match = ua.match(/Mac OS X (\d+[._]\d+)/);
      osVersion = match ? match[1].replace('_', '.') : null;
    } else if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) {
      osName = 'iOS';
      const match = ua.match(/OS (\d+_\d+)/);
      osVersion = match ? match[1].replace('_', '.') : null;
    } else if (ua.includes('Android')) {
      osName = 'Android';
      const match = ua.match(/Android (\d+\.\d+)/);
      osVersion = match ? match[1] : null;
    } else if (ua.includes('Linux')) {
      // Only set Linux if we can be more specific
      // Check if it's a known Linux distribution
      if (ua.includes('Ubuntu') || ua.includes('Debian') || ua.includes('Fedora') || ua.includes('CentOS')) {
        osName = 'Linux';
        // Try to extract distribution name
        const distMatch = ua.match(/(Ubuntu|Debian|Fedora|CentOS)/);
        if (distMatch) {
          osName = distMatch[1];
        }
      } else {
        // Generic Linux - return null to avoid "Linux unknown"
        osName = null;
      }
    }

    // Don't return OS info if we couldn't determine it
    if (!osName) {
      return null;
    }

    return {
      name: osName,
      version: osVersion,
      platform
    };
  }

  /**
   * Get device information
   * @returns {Object} - Device details
   * @private
   */
  getDeviceInfo() {
    if (typeof window === 'undefined' || !window.navigator) {
      return null;
    }

    const ua = window.navigator.userAgent;
    let deviceType = 'desktop';
    let deviceModel = null;

    // Detect device type with improved accuracy
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Opera Mini/.test(ua)) {
      deviceType = 'mobile';
      
      if (/(iPhone|iPod)/.test(ua)) {
        const match = ua.match(/(iPhone|iPod)/);
        deviceModel = match ? match[0] : null;
      } else if (/iPad/.test(ua)) {
        deviceType = 'tablet';
        deviceModel = 'iPad';
      } else if (/Android/.test(ua)) {
        // Try to extract Android device model
        const androidMatch = ua.match(/Android[^;]*;\s*([^)]+)/);
        if (androidMatch && androidMatch[1]) {
          const model = androidMatch[1].trim();
          
          // Filter out generic/build info patterns
          if (!this.isGenericValue(model, 'deviceModel')) {
            deviceModel = model;
            
            // Check if it's a tablet based on model name or screen size
            if (/tablet|tab/i.test(model) || (window.screen && Math.min(window.screen.width, window.screen.height) >= 768)) {
              deviceType = 'tablet';
            }
          }
          // If generic, leave deviceModel as null
        }
      }
    } else if (/iPad|Tablet/.test(ua)) {
      deviceType = 'tablet';
      // Don't set generic "Tablet" as model
      if (/iPad/.test(ua)) {
        deviceModel = 'iPad';
      }
    }

    return {
      type: deviceType,
      model: deviceModel,
      memory: window.navigator.deviceMemory || null,
      cores: window.navigator.hardwareConcurrency || null,
      online: window.navigator.onLine
    };
  }

  /**
   * Get screen information
   * @returns {Object} - Screen details
   * @private
   */
  getScreenInfo() {
    if (typeof window === 'undefined') {
      return null;
    }

    return {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      orientation: window.screen.orientation?.type || null,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }

  /**
   * Check if a value is generic/placeholder
   * @param {*} value - The value to check
   * @param {string} field - Field type (deviceModel, osName, etc.)
   * @returns {boolean} - True if value is generic
   * @private
   */
  isGenericValue(value, field) {
    if (!value) {
      return true;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      
      // Empty or "unknown" is generic
      if (!trimmed || trimmed.toLowerCase() === 'unknown') {
        return true;
      }

      // Check field-specific patterns
      if (field && this.genericPatterns[field]) {
        return this.genericPatterns[field].some(pattern => pattern.test(trimmed));
      }
    }

    return false;
  }

  /**
   * Clean context data by removing generic values and empty objects
   * @param {Object} context - Raw context data
   * @returns {Object} - Cleaned context data
   * @private
   */
  cleanContextData(context) {
    if (!context) {
      return {};
    }

    const cleaned = {};

    // Clean browser context
    if (context.browser && typeof context.browser === 'object') {
      const browser = { ...context.browser };
      
      // Remove generic browser name
      if (this.isGenericValue(browser.name, 'browserName')) {
        browser.name = null;
      }
      
      // Only include browser if it has meaningful data
      if (browser.name || browser.version || browser.engine) {
        cleaned.browser = browser;
      }
    }

    // Clean device context
    if (context.device && typeof context.device === 'object') {
      const device = { ...context.device };
      
      // Remove generic device model
      if (this.isGenericValue(device.model, 'deviceModel')) {
        device.model = null;
      }
      
      // Always include device type and other info
      cleaned.device = device;
    }

    // Clean OS context
    if (context.os && typeof context.os === 'object') {
      const os = { ...context.os };
      
      // Remove generic OS name
      if (this.isGenericValue(os.name, 'osName')) {
        os.name = null;
      }
      
      // Only include OS if it has meaningful data
      if (os.name || os.version || os.platform) {
        cleaned.os = os;
      }
    }

    // Include screen context (usually reliable)
    if (context.screen) {
      cleaned.screen = context.screen;
    }

    // Include user context if present
    if (context.user && typeof context.user === 'object') {
      const user = {};
      
      // Only include non-null user fields
      if (context.user.id) user.id = context.user.id;
      if (context.user.email) user.email = context.user.email;
      if (context.user.username) user.username = context.user.username;
      if (context.user.ip_address) user.ip_address = context.user.ip_address;
      
      if (Object.keys(user).length > 0) {
        cleaned.user = user;
      }
    }

    // Include custom context
    if (context.custom && typeof context.custom === 'object' && Object.keys(context.custom).length > 0) {
      cleaned.custom = context.custom;
    }

    // Include breadcrumbs if present
    if (context.breadcrumbs) {
      cleaned.breadcrumbs = context.breadcrumbs;
    }

    return cleaned;
  }

  /**
   * Set user information for error tracking
   * @param {Object|null} user - User data with id, email, username
   * @param {string} [user.id] - User ID
   * @param {string} [user.email] - User email
   * @param {string} [user.username] - Username
   * @public
   */
  setUser(user) {
    if (!user) {
      this.context.user = null;
      return;
    }

    this.context.user = {
      id: user.id || null,
      email: user.email || null,
      username: user.username || user.name || null
    };
  }

  /**
   * Clear user context (e.g., on logout)
   * @public
   */
  clearUser() {
    this.context.user = null;
  }

  /**
   * Set custom context data
   * @param {Object} data - Custom context data
   * @public
   */
  setCustomContext(data) {
    this.context.custom = { ...this.context.custom, ...data };
  }

  /**
   * Collect the current context data with cleaning
   * @returns {Object} - Collected and cleaned context information
   * @public
   */
  collectContext() {
    // Update dynamic values like screen size which might change during session
    this.context.screen = this.getScreenInfo();
    
    // Create a deep copy
    const contextCopy = JSON.parse(JSON.stringify(this.context));
    
    // Clean the context data to remove generic values
    return this.cleanContextData(contextCopy);
  }
}

export default ContextCollector;
